import React, { useEffect, useState, useCallback, useRef } from "react";
import Input from "../../components/controls/input/customInput/CustomInput";
import decisionLogService from "../../store/services/decisionLog.service";
import { CalendarIcon } from "../../modules/default-svg";
import _ from "lodash";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import { calculatePageSize } from "../../helpers/utils";
import Table from "../../components/controls/tables/Table";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import Skeleton from "react-loading-skeleton";
import {

} from "../../store/actions/decisionLog.actions";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import Searchable from "../../components/controls/select/CustomSelect";
import projectService from "../../store/services/projects.service";

const DecisionLog = (props) => {
  const {
    projectID,
    programID,
    allDecisionLogs,
    loading,
    pagination,
    pageLoading,
    getDecisionLogRequest,
    createDecisionLogRequest,
    updateDecisionLogRequest,
    deleteDecisionLogRequest, } = props;

  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [perPageItems] = useState(calculatePageSize());
  const [pageNum, setPageNum] = useState(1);
  const [selectedRow, setSelectedRow] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isCreateNewRow, setIsCreateNewRow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeletedId] = useState(null);
  const [parentType, setParentType] = useState("Project"); // Default to Project
  const [formErrors, setFormErrors] = useState({
    title: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const currentContextRef = useRef(null);
  const [decisionLogDto, setDecisionLogDto] = useState({
    title: "",
    owner: "",
    datePresented: null,
    dateDecided: null,
    comments: "",
    projectId: projectID,
    programId: programID,
  });
  const [decisionLogUpdateDto, setDecisionLogUpdateDto] = useState({
    title: "",
    owner: "",
    datePresented: null,
    dateDecided: null,
    comments: "",
    projectId: projectID,
    programId: programID,
  });
  const [decisionLogCreateDto, setDecisionLogCreateDto] = useState({
    title: "",
    owner: "",
    datePresented: null,
    dateDecided: null,
    comments: "",
    projectId: projectID,
    programId: programID,
  });
  const [tableSearch, setTableSearch] = useState(null);

  const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
    const payload = { page: pageNumber ?? pageNum, size: pageSize ?? perPageItems, search: forceClearSearch ? null : (searchTerm ?? tableSearch) };
    payload.parent = parentType;

    if (programID) {
      payload.id = programID;
      payload.parent = "Program";
    }

    else if (projectID) {
      payload.id = projectID;
      payload.parent = "Project";
    }
    else {
      setData([]);
      return;
    }

    getDecisionLogRequest(payload);
  };
  // Update DTOs when context changes
  useEffect(() => {
    const projectIdToUse = parentType === "Program" ? selectedProject?.value : projectID;

    setDecisionLogDto(prev => ({
      ...prev,
      projectId: projectIdToUse,
      programId: programID,
    }));
    setDecisionLogUpdateDto(prev => ({
      ...prev,
      projectId: projectIdToUse,
      programId: programID,
    }));
    setDecisionLogCreateDto(prev => ({
      ...prev,
      projectId: projectIdToUse,
      programId: programID,
    }));
  }, [projectID, programID, parentType, selectedProject]);

  // Set parentType based on context
  useEffect(() => {
    if (projectID) {
      setParentType("Project");
    } else if (programID) {
      setParentType("Program");
    }
    // Add more context detection if needed (Portfolio, etc.)
  }, [projectID, programID]);

  // Update DTOs when selectedProject changes
  useEffect(() => {
    if (parentType === "Program" && selectedProject) {
      const projectIdToUse = selectedProject.value;
      setDecisionLogDto(prev => ({
        ...prev,
        projectId: projectIdToUse,
      }));
      setDecisionLogUpdateDto(prev => ({
        ...prev,
        projectId: projectIdToUse,
      }));
      setDecisionLogCreateDto(prev => ({
        ...prev,
        projectId: projectIdToUse,
      }));
    }
  }, [selectedProject, parentType]);


  const handleAddRow = useCallback(() => {
    setIsCreateNewRow(true);
    fetchData(pagination.TotalPages ?? 1, perPageItems > 10 ? perPageItems - 5 : perPageItems - 3);
  }, [projectID, programID, pagination, perPageItems]);

  const handleDelete = (selectedRow) => {
    const id = selectedRow.id;
    if (projectID) {
      deleteDecisionLogRequest({ projectId: projectID, programId: programID, id: id });
    } else if (programID) {
      // When under program context, use the project associated with the Decision Log
      const projectIdToUse = selectedRow.project?.id || selectedProject?.value;
      if (projectIdToUse) {
        deleteDecisionLogRequest({ projectId: projectIdToUse, programId: programID, id: id });
      } else {
        dispatch(
          generalActions.showToast({
            title: "Unable to determine project for this Decision Log",
            type: "error",
          })
        );
        return;
      }
    }
    setDeletedId(id);
  };

  const initDataState = () => {
    setFormErrors({});
    const contextId = projectID || programID;
    setSelectedProject(null);

    // Determine the projectId to use
    const projectIdToUse = parentType === "Program" ? selectedProject?.value : projectID;

    setDecisionLogDto({
      title: "",
      owner: "",
      datePresented: null,
      dateDecided: null,
      comments: "",
      projectId: projectIdToUse,
      programId: programID,
    });
    setDecisionLogCreateDto({
      title: "",
      owner: "",
      datePresented: null,
      dateDecided: null,
      comments: "",
      projectId: projectIdToUse,
      programId: programID,
    });
    setDecisionLogUpdateDto({
      title: "",
      owner: "",
      datePresented: null,
      dateDecided: null,
      comments: "",
      projectId: projectIdToUse,
      programId: programID,
    });
    setAttachments([]);
  };

  const handleCreate = (val) => {
    initDataState();
    setIsCreate(val);
  };

  const handleSave = () => {
    const isEdit = Object.keys(selectedRow).length > 0;

    const errors = {};
    if (isEdit ? !decisionLogUpdateDto.title : !decisionLogCreateDto.title)
      errors["title"] = `Required field`;

    // When under program context, require project selection
    if (parentType === "Program" && !selectedProject) {
      errors["projectId"] = "Project is required";
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Determine the projectId to use
    const projectIdToUse = parentType === "Program" ? selectedProject?.value : projectID;

    if (isEdit) {
      const updatePayload = {
        ...decisionLogUpdateDto,
        projectId: projectIdToUse,
      };

      updateDecisionLogRequest({
        id: selectedRow.id,
        projectId: projectIdToUse,
        programId: programID,
        payload: updatePayload,
      });

      return;
    } else {
      const createPayload = {
        ...decisionLogCreateDto,
        projectId: projectIdToUse,
      };

      createDecisionLogRequest({
        projectId: projectIdToUse,
        programId: programID,
        payload: createPayload,
      });

    }
  };

  const fetchDecisionLogData = async () => {
    setIsLoading(true);
    decisionLogService
      .fetchSingleDecisionLogRequest(selectedRow.id)
      .then((data) => {
        setDecisionLogDto({
          title: data.title,
          owner: data.owner,
          datePresented: data.datePresented,
          dateDecided: data.dateDecided,
          comments: data.comments,
          projectId: projectID,
          programId: programID,
        });
        setAttachments(data.attachments || []);
        setDecisionLogUpdateDto({
          title: data.title,
          owner: data.owner,
          datePresented: data.datePresented,
          dateDecided: data.dateDecided,
          comments: data.comments,
          projectId: projectID,
          programId: programID,
        });

        // If under program context and we have project data, populate the selected project
        if (parentType === "Program" && data.project) {
          setSelectedProject({
            label: data.project.name,
            value: data.project.id,
          });
        }
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    setIsEdit(false);
    initDataState();
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      initDataState();
      isEdit && fetchDecisionLogData();
    }
  }, [selectedRow, isEdit]);

  useEffect(() => {
    fetchData(pageNum, perPageItems);
  }, [pageNum, perPageItems, projectID, programID]);

  // Reset data when context changes to ensure proper navigation
  useEffect(() => {
    setData([]);
    setSelectedProject(null);
    setSelectedRow({});
    setIsEdit(false);
    setIsCreate(false);
    setIsCreateNewRow(false);
    setDeletedId(null);
    setFormErrors({});
    setAttachments([]);
  }, [projectID, programID]);

  // Track current context
  useEffect(() => {
    const newContext = projectID || programID;
    if (currentContextRef.current !== newContext) {
      currentContextRef.current = newContext;
      setData([]);
    }
  }, [projectID, programID]);

  useEffect(() => {
    if (allDecisionLogs && allDecisionLogs.length > 0) {
      if (isCreateNewRow) {
        setData(allDecisionLogs.sort((a, b) => b.isFlagged - a.isFlagged));
        setIsCreateNewRow(false);
      } else {
        // Always replace data when context has changed or when we have new data
        setData(allDecisionLogs.sort((a, b) => b.isFlagged - a.isFlagged));
      }
    } else if (allDecisionLogs && allDecisionLogs.length === 0) {
      setData([]);
    }
  }, [allDecisionLogs, isCreateNewRow, deleteId]);

  const handleCancelAddRow = () => {
    setIsCreateNewRow(false);
    fetchData();
  };

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };

  const searchProjectHandler = (value) => {
    if (value === "") {
      setProjectOptions([]);
      return;
    }
    projectService
      .searchProjectsRequest({
        searchText: value,
        parent: "Program",
        id: programID
      })
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setProjectOptions(transformedArray);
      })
      .catch(() => {
        setProjectOptions([]);
      });
  };

  const handleInputChange = (name, value) => {
    if (isEdit) {
      setDecisionLogUpdateDto((prev) => ({ ...prev, [name]: value }));
    } else {
      setDecisionLogCreateDto((prev) => ({ ...prev, [name]: value }));
    }
    setDecisionLogDto((prev) => ({ ...prev, [name]: value }));
  };

  const formBody = () => {
    return (
      <>
        <div className="d-flex flex-col">
          {parentType === "Program" && (
            <div className="row w-100 mb-3 gx-3">
              <div className="col-12">
                <Searchable
                  label="Project"
                  SelectText="project"
                  searchText={searchProjectHandler}
                  options={projectOptions}
                  defaultVal={selectedProject}
                  onChange={(e) => setSelectedProject(e)}
                  errorMessage={formErrors.projectId}
                  isRequired
                  loading={isLoading}
                />
              </div>
            </div>
          )}
          <div className="row w-100 mb-3 gx-3">
            <div className="col-4">
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter Owner"
                value={decisionLogDto.owner}
                type="text"
                handleChange={(value) => {
                  handleInputChange("owner", value);
                }}
                label="Owner"
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                icon={<CalendarIcon />}
                value={decisionLogDto.datePresented}
                maxDate={decisionLogDto.dateDecided}
                type="date"
                handleChange={(value) => {
                  handleInputChange("datePresented", value);
                }}
                label="Date Presented"
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                icon={<CalendarIcon />}
                value={decisionLogDto.dateDecided}
                minDate={decisionLogDto.datePresented}
                type="date"
                handleChange={(value) => {
                  handleInputChange("dateDecided", value);
                }}
                label="Date Decided"
                loading={isLoading}
              />
            </div>
          </div>
          <div className="row w-100 mb-3 gx-3">
            <RichTextEditor
              label="Decision"
              required
              content={decisionLogDto.title}
              onChange={(value) => {
                handleInputChange("title", value);
              }}
              errorMessage={formErrors.title}
              loading={isLoading}
            />
          </div>
          <div className="row w-100 mb-3 gx-3">
            <RichTextEditor
              label="Comments"
              content={decisionLogDto.comments}
              onChange={(value) => {
                handleInputChange("comments", value);
              }}
              loading={isLoading}
            />
          </div>
          <EntityFileUpload
            entityId={selectedRow.id}
            entityType="Decision"
            attachments={attachments}
            onAttachmentsChange={() => {
              if (selectedRow.id) {
                decisionLogService
                  .fetchSingleDecisionLogRequest(selectedRow.id)
                  .then((decisionLogData) => {
                    setAttachments(decisionLogData.attachments);
                  })
                  .catch((error) => {
                    console.error("Error fetching decision log data:", error);
                  });
              }
            }}
            disabled={isLoading || !selectedRow.id}
          />
        </div>
      </>
    );
  };
  const handleSearchTrigger = (searchTerm) => {
    if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

    let pageNumber = pageNum;
    if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
      setData([]); //clear existing data
      pageNumber = 1; //reset to first page
    }

    setTableSearch(searchTerm);
    fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
  };


  return (
    <ColumnConfigProvider
      tableName={parentType ? `${parentType}-Decision` : "Decision"}
    >
      <div className="listingScreen position-relative">
        <Table
          type="Decision"
          rows={data || []}
          body={formBody()}
          searchHandler={handleSearch}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          setPageNum={setPageNum}
          perPageItems={perPageItems}
          setIsCreate={(val) => {
            handleCreate(val);
          }}
          loading={pageLoading}
          modalLoading={loading}
          isCreate={isCreate}
          pagination={pagination}
          handleSave={() => handleSave()}
          handleDelete={(selectedRow) => handleDelete(selectedRow)}
          handleCancel={() => handleCancel()}
          setIsEdit={setIsEdit}
          setSelectedRow={setSelectedRow}
          projectId={projectID}
          programId={programID}
          parentType={parentType}
          handleAddRow={handleAddRow}
          handleCancelAddRow={handleCancelAddRow}
          setData={setData}
          triggerSearch={handleSearchTrigger}
        />
      </div>
    </ColumnConfigProvider>
  );
};

export default DecisionLog; 